import { useState, useEffect } from "react";
import { useToast } from "../hooks/use-toast";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Progress } from "../components/ui/progress";
import { ImagePlus, Link as LinkIcon } from "lucide-react";
import { cn } from "../lib/utils";

interface SearchFormProps {
  onSearch: (data: { file?: File; url?: string; removeNsfw: boolean }) => void;
  isLoading: boolean;
  isDisabled?: boolean;
  isPasteDisabled?: boolean;
}

export function SearchForm({ onSearch, isLoading, isDisabled }: SearchFormProps) {
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [removeNsfw, setRemoveNsfw] = useState(() => {
    const saved = localStorage.getItem("removeNsfw");
    return saved === null ? true : saved === "true";
  });
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem("removeNsfw", removeNsfw.toString());
  }, [removeNsfw]);

  // Handle pasted images
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (isDisabled || isLoading) {
        e.preventDefault();
        return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            setUrl(""); // Clear URL when file is pasted
            setFile(file);
            onSearch({ file, removeNsfw });
            toast({
              title: "Image pasted",
              description: "Searching with pasted image...",
            });
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [onSearch, removeNsfw]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    setUrl(""); // Clear URL when file is selected
    setFile(selectedFile);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("remove_nsfw", removeNsfw ? "1" : "0");

    setUploadProgress(0);
    onSearch({ file: selectedFile, removeNsfw });
  };

  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) return;

    try {
      new URL(url);
      setFile(null); // Clear file when URL is submitted
      const input = document.getElementById("file-upload") as HTMLInputElement;
      if (input) input.value = ""; // Clear file input
      onSearch({ url, removeNsfw });
    } catch {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid image URL",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className={cn("w-full max-w-2xl mx-auto", isDisabled && "opacity-50")}>
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="file-upload" className="block mb-2">
              Upload Image
            </Label>
            <div className="flex items-center gap-4">
              <Input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={isLoading || isDisabled}
                className="cursor-pointer"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => document.getElementById("file-upload")?.click()}
                disabled={isLoading || isDisabled}
              >
                <ImagePlus className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              You can also paste an image from clipboard (Ctrl+V)
            </p>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <Progress value={uploadProgress} className="mt-2" />
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or
              </span>
            </div>
          </div>

          <form onSubmit={handleUrlSubmit} className="space-y-2">
            <Label htmlFor="image-url">Image URL</Label>
            <div className="flex gap-2">
              <Input
                id="image-url"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={isLoading || isDisabled}
                className="placeholder:text-gray-400"
              />
              <Button type="submit" disabled={isLoading || isDisabled || !url}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </form>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="nsfw-filter"
            checked={removeNsfw}
            onCheckedChange={setRemoveNsfw}
            disabled={isDisabled}
          />
          <Label htmlFor="nsfw-filter">Remove NSFW results</Label>
        </div>
      </CardContent>
    </Card>
  );
}